/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AccessListItem } from '../components/accessList/invite-user.component';
import { XdaStringsService } from './xda-strings.service';
import { User, UserService, AccessListRole, UserStatus } from './user.service';
import { AppsService } from './apps.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorService } from '../services/error.service';

@Injectable({
  providedIn: 'root',
})
export class AccessListService {
  baseUrl = 'api/accesslist/';
  configBaseUrl = 'api/accesslistconfig/';
  refreshAccessList: EventEmitter<boolean>;
  refreshAccessListSuccess: EventEmitter<boolean>;
  configUpdated: EventEmitter<AccessListConfig>;
  config: AccessListConfig;
  private configTime = 0;
  private configStalePeriod: number = 5 * 60 * 1000; // 5 mins
  private configPromiseAlreadyExists?: Promise<AccessListConfig | null>;

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private xdaStringsService: XdaStringsService,
    private userService: UserService,
    private appsService: AppsService,
    private errorService: ErrorService,
  ) {
    this.refreshAccessList = new EventEmitter();
    this.refreshAccessListSuccess = new EventEmitter();
    this.configUpdated = new EventEmitter();
  }

  async discoverAccessLists(userId: string) {
    const appId = this.xdaStringsService.strings['APPID'];
    return await this.http
      .get(`${this.baseUrl}discover`, {
        params: { userId: userId, appId: appId },
      })
      .toPromise();
  }

  updateEula(userId: string) {
    const appId = this.xdaStringsService.strings['APPID'];
    return this.http.post(`${this.baseUrl}update-eula`, {
      userId: userId,
      appId: appId,
    });
  }

  async getAccessListUsers(accessListId: string) {
    return await this.http
      .get(`${this.baseUrl}users`, { params: { accessListId: accessListId } })
      .toPromise();
  }

  getAccessListUsersUsage(accessListId: string, includeUsage?: boolean) {
    const accessListUsersParams = {
      accessListId: accessListId,
      includeUsage: false,
    };
    if (includeUsage) {
      accessListUsersParams.includeUsage = includeUsage;
    }
    return this.http.get(`${this.baseUrl}users`, {
      params: accessListUsersParams,
    });
  }

  async canInviteUser(email: string, accessListId: string, appId: string) {
    return await this.http
      .get(`${this.baseUrl}can-invite`, {
        params: { email: email, accessListId: accessListId, appId: appId },
      })
      .toPromise();
  }

  async addToAccessList(accessListItem: AccessListItem) {
    return await this.http
      .post(`${this.baseUrl}add`, accessListItem)
      .toPromise();
  }

  async removeFromAccessList(accessListItem: AccessListItem) {
    return await this.http
      .post(`${this.baseUrl}remove`, accessListItem)
      .toPromise();
  }

  async discover(user: User) {
    try {
      const json = await this.discoverAccessLists(user.id);
      if (json && Array.isArray(json)) {
        const list = json;
        if (list && list.length) {
          const userAcceptedEula = list.find((x) => x.eulaAcceptedDateTimeUtc);
          if (userAcceptedEula) {
            const currentUser = this.userService.user;
            if (currentUser) {
              currentUser.isSubscriptionUser = true;
            }
            const item = list.find(
              (x) =>
                x.accessListRole === AccessListRole.Owner ||
                x.accessListRole === AccessListRole.Manager,
            );
            if (item) {
              if (currentUser) {
                currentUser.accessListRole = item.accessListRole;
                currentUser.accessListId = item.accessListId;
              }
            } else {
              if (currentUser) {
                currentUser.accessListRole = AccessListRole.User;
                currentUser.accessListId = list[0].accessListId;
              }
            }
            await this.appsService.getActiveAcquisitions(user.id);
            this.userService.discovered.emit(true);
            return 'discovered';
          } else {
            return 'eula-required';
          }
        }
      }
      return 'not-discovered';
    } catch (error) {
      console.log(error);
      this.errorService.displayGeneralError().result.then(() => {
        this.modalService.dismissAll();
      });
      return 'not-discovered';
    }
  }

  emitRefreshEvent() {
    this.refreshAccessList.emit(true);
  }

  emitRefreshSuccessEvent() {
    this.refreshAccessListSuccess.emit(true);
  }

  async getAccessConfigConfig(accessListId: string, emit?: boolean) {
    // if user doesn't have an access list then they have no config
    if (!accessListId) {
      return Promise.resolve(null);
    }

    const isStale = Date.now() - this.configTime >= this.configStalePeriod;

    // If we have config is not stale, return it immediately
    if (!isStale && this.config !== undefined) {
      return this.config;
    }

    // If request is in progress, wait for the response
    if (this.configPromiseAlreadyExists) {
      const result = await this.configPromiseAlreadyExists;
      // only emit configUpdated if emit is true to prevent unnecessary updates
      if (emit === true) {
        this.configUpdated.emit(result ?? undefined);
      }
      return result;
    }

    this.configPromiseAlreadyExists = (async () => {
      try {
        const response = await this.http
          .get<AccessListConfig>(
            `${this.configBaseUrl}retrieve/${accessListId}`,
          )
          .toPromise();
        if (response) {
          this.config = response;
          this.configTime = Date.now();
          // only emit configUpdated if emit is true to prevent unnecessary updates
          if (emit === true) {
            this.configUpdated.emit(response ?? undefined);
          }
        } else {
          // if no config is returned (new account or one that has never saved a config (even an empty one)) then emit an empty config if emit is true
          if (emit === true) {
            this.configUpdated.emit({
              hiddenWorkflows: [],
              disableCloudConnectors: [],
              disableEncryptedEmail: false,
              disableAutoWorkflows: false,
              enableTenant: false,
              tenantId: '',
              enableDomainRestrictions: false,
              allowedDomains: [],
            });
          }
        }

        return response;
      } catch (err) {
        console.error('Error occured getting accesslist configurations', err);
        return null;
      } finally {
        this.configPromiseAlreadyExists = undefined;
      }
    })();

    return this.configPromiseAlreadyExists;
  }

  async insertAccessConfigConfig(accessId: string, config: AccessListConfig) {
    this.config = config;
    return await this.http
      .post(`${this.configBaseUrl}insert`, {
        accessId: accessId,
        config: config,
      })
      .toPromise();
  }

  async refreshAccessConfigConfig(
    accessListId?: string,
    emitUpdate?: boolean,
  ): Promise<void> {
    this.getAccessConfigConfig(
      accessListId ?? this.userService.user?.accessListId ?? '',
      emitUpdate,
    );
  }

  isAllowedDomain(domain: string): boolean {
    // if email address extract the domain
    if (domain.indexOf('@') !== -1) {
      const email = domain.split('@');
      domain = email[1];
    }
    const allowedDomains =
      this.config?.enableDomainRestrictions &&
      this.config?.allowedDomains !== undefined
        ? this.config.allowedDomains
        : [];

    // if not advanced plan or domain restrictions is disabled or domain is found then it's allowed
    return !this.userService.isAdvancedPlan ||
      !this.config?.enableDomainRestrictions ||
      allowedDomains.find((x) => x.toLowerCase() == domain.toLowerCase())
      ? true
      : false;
  }
}
export interface AccessListConfig {
  hiddenWorkflows: string[];
  disableCloudConnectors: string[];
  disableEncryptedEmail: boolean;
  disableAutoWorkflows: boolean;
  enableTenant: boolean;
  tenantId: string;
  enableDomainRestrictions: boolean;
  allowedDomains: string[];
}

export interface AccessListUserType {
  accessListId: string;
  userId: string;
  appId: string;
  managerId: string;
  accessListRole: AccessListRole;
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  eulaAcceptedDateTimeUtc: null | string;
  userStatus: UserStatusType;
  checked?: boolean;
  userStatusTranslated?: string;
  accessListRoleTranslated?: string;
  accessListPermissionTranslated?: string;
  userSort?: string;
}

export interface UserStatusType {
  name: UserStatus;
  value: UserStatus;
  sdekey: string;
}
